import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Assessment, 
  VpnKey, 
  Description, 
  Notifications,
  BarChart,
  Lock
} from '@mui/icons-material';

const Dashboard: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Welcome Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" gutterBottom>
              Welcome to the ReasonShield Vendor Portal
            </Typography>
            <Typography variant="body1" paragraph>
              This portal provides tools and resources for ReasonShield vendors to integrate with our laboratory systems.
            </Typography>
            <Typography variant="body1">
              Use the cards below to navigate to different sections of the portal.
            </Typography>
          </Paper>
        </Grid>

        {/* Quick Access Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VpnKey color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">API Keys</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Manage your API keys for accessing our services.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={RouterLink} 
                to="/lis-portal/api-keys"
              >
                Manage Keys
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Lock color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">Webhook Secrets</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Manage your webhook secrets for signature verification.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={RouterLink} 
                to="/lis-portal/webhook-secrets"
              >
                Manage Secrets
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">Usage Statistics</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                View your API usage statistics and quotas.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={RouterLink} 
                to="/lis-portal/usage"
              >
                View Stats
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Description color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">Documentation</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Access API documentation and integration guides.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={RouterLink} 
                to="/lis-portal/documentation"
              >
                View Docs
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChart color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">Reports</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Access analytical reports and dashboards.
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={RouterLink} 
                to="/reports/antimicrobial-stewardship"
              >
                View Reports
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" gutterBottom>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem>
                <ListItemIcon>
                  <BarChart color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="New Report Available" 
                  secondary="Antimicrobial Stewardship Report is now available for your organization" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Notifications color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="System Maintenance Scheduled" 
                  secondary="Scheduled maintenance on June 15, 2023 from 2:00 AM to 4:00 AM UTC" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VpnKey color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="API Key Rotation Reminder" 
                  secondary="Remember to rotate your API keys every 365 days for security" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Description color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="New API Documentation Available" 
                  secondary="Updated documentation for the latest API version is now available" 
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ pt: 4 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {'© '}
          {new Date().getFullYear()}
          {' ReasonShield Vendor Portal. All rights reserved.'}
        </Typography>
      </Box>
    </Container>
  );
};

export default Dashboard; 